import { useEffect, useRef, useState } from 'react';
import { FiCheck, FiEye, FiEyeOff, FiLoader, FiX } from 'react-icons/fi';
import { checkUsername } from '../../lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,40}$/;

function Field({ label, error, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink dark:text-white">{label}</span>
      {children}
      {error && <span className="text-xs text-danger">{error}</span>}
    </label>
  );
}

const inputClass =
  'w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-100 dark:border-midnight-600 dark:bg-midnight-800 dark:text-white dark:focus:ring-brand-900/40';

export default function RegistrationForm({ accountType, values, onChange, errors, onValidityChange }) {
  const [showPassword, setShowPassword] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle | checking | available | taken | invalid
  const debounceRef = useRef(null);

  useEffect(() => {
    const username = values.username.trim();
    clearTimeout(debounceRef.current);

    if (!username) {
      setUsernameStatus('idle');
      onValidityChange('username', false);
      return;
    }
    if (!USERNAME_RE.test(username)) {
      setUsernameStatus('invalid');
      onValidityChange('username', false);
      return;
    }

    setUsernameStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await checkUsername(username);
        setUsernameStatus(result.available ? 'available' : 'taken');
        onValidityChange('username', result.available);
      } catch {
        setUsernameStatus('idle');
        onValidityChange('username', false);
      }
    }, 450);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.username]);

  const usernameHint = {
    checking: { icon: <FiLoader className="animate-spin" size={14} />, text: 'Checking availability…', className: 'text-subtle' },
    available: { icon: <FiCheck size={14} />, text: 'Username available', className: 'text-success' },
    taken: { icon: <FiX size={14} />, text: 'That username is already taken', className: 'text-danger' },
    invalid: {
      icon: <FiX size={14} />,
      text: '3-40 characters: letters, numbers, dots, underscores, hyphens',
      className: 'text-danger',
    },
  }[usernameStatus];

  const passwordsMatch = values.password && values.password === values.confirmPassword;

  return (
    <div className="flex flex-col gap-5">
      {accountType === 'organization' && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Organization name" error={errors.orgName}>
            <input
              type="text"
              className={inputClass}
              value={values.orgName}
              onChange={(e) => onChange('orgName', e.target.value)}
              placeholder="Acme Logistics Ltd"
              maxLength={100}
            />
          </Field>
          <Field label="Organization billing email" error={errors.orgEmail}>
            <input
              type="email"
              className={inputClass}
              value={values.orgEmail}
              onChange={(e) => onChange('orgEmail', e.target.value)}
              placeholder="billing@acme.com"
              maxLength={100}
            />
          </Field>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={accountType === 'organization' ? 'Your full name (admin)' : 'Full name'} error={errors.fullname}>
          <input
            type="text"
            className={inputClass}
            value={values.fullname}
            onChange={(e) => onChange('fullname', e.target.value)}
            placeholder="Jane Muthoni"
            maxLength={100}
          />
        </Field>

        <Field label="Username">
          <input
            type="text"
            className={inputClass}
            value={values.username}
            onChange={(e) => onChange('username', e.target.value)}
            placeholder="jane.muthoni"
            maxLength={40}
            autoComplete="username"
          />
          {usernameHint && (
            <span className={`flex items-center gap-1.5 text-xs ${usernameHint.className}`}>
              {usernameHint.icon}
              {usernameHint.text}
            </span>
          )}
        </Field>
      </div>

      <Field label={accountType === 'organization' ? 'Your email' : 'Email'} error={errors.email}>
        <input
          type="email"
          className={inputClass}
          value={values.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="jane@example.com"
          maxLength={100}
          autoComplete="email"
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Password" error={errors.password}>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`${inputClass} pr-11`}
              value={values.password}
              onChange={(e) => onChange('password', e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-subtle hover:text-ink dark:hover:text-white"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
        </Field>

        <Field label="Confirm password" error={values.confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined}>
          <input
            type={showPassword ? 'text' : 'password'}
            className={inputClass}
            value={values.confirmPassword}
            onChange={(e) => onChange('confirmPassword', e.target.value)}
            placeholder="Re-enter password"
            autoComplete="new-password"
          />
        </Field>
      </div>
    </div>
  );
}

export function validateRegistrationForm(accountType, values) {
  const errors = {};

  if (!values.fullname.trim()) errors.fullname = 'Full name is required';
  if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Enter a valid email address';
  if (values.password.length < 8) errors.password = 'Password must be at least 8 characters';

  if (accountType === 'organization') {
    if (!values.orgName.trim()) errors.orgName = 'Organization name is required';
    if (!EMAIL_RE.test(values.orgEmail.trim())) errors.orgEmail = 'Enter a valid organization email';
  }

  return errors;
}
